/**
 * Opening the consent screen and waiting for the answer.
 *
 * A popup rather than a full-page redirect: the time cursor lives in memory and
 * in sessionStorage, and bouncing the whole tab through accounts.google.com
 * would land the user back at the top of their timeline instead of where they
 * were. The popup posts its result back and closes itself.
 */

import { useCallback } from "react";

import { hgApi, useStartAuthorizationMutation } from "@/api/api";
import { useAppDispatch } from "@/state/redux/hooks";
import { forgetAllTokens } from "./tokens";

export const OAUTH_MESSAGE = "eventera-oauth";
export const OAUTH_CALLBACK_PATH = "/oauth/callback";

export interface OAuthResult {
  status: "ok" | "error";
  connection?: string;
  system?: string;
  reason?: string;
}

const POPUP_FEATURES = "width=520,height=700,menubar=no,toolbar=no";

function waitForResult(popup: Window): Promise<OAuthResult> {
  return new Promise((resolve) => {
    let settled = false;

    const finish = (result: OAuthResult) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onMessage);
      clearInterval(poll);
      resolve(result);
    };

    const onMessage = (event: MessageEvent) => {
      // The callback page is served from this same origin, so anything else is
      // some other frame talking and must not be able to fake a grant.
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== OAUTH_MESSAGE) return;
      finish(event.data as OAuthResult);
    };

    window.addEventListener("message", onMessage);

    // The user can close the popup without answering, and no message is sent
    // then; without this the promise would never settle and the button would
    // spin forever.
    const poll = setInterval(() => {
      if (popup.closed) finish({ status: "error", reason: "cancelled" });
    }, 500);
  });
}

export function useConnectProvider() {
  const dispatch = useAppDispatch();
  const [startAuthorization] = useStartAuthorizationMutation();

  return useCallback(
    async (systemId: string, scopes: string[]): Promise<OAuthResult> => {
      // Opened before the await: a popup opened after one is no longer tied to
      // the click that asked for it, and browsers block it.
      const popup = window.open("about:blank", OAUTH_MESSAGE, POPUP_FEATURES);
      if (!popup) {
        return { status: "error", reason: "popup_blocked" };
      }

      try {
        const { url } = await startAuthorization({
          system_id: systemId,
          scopes,
          return_to: window.location.pathname + window.location.search,
        }).unwrap();
        popup.location.replace(url);
      } catch {
        popup.close();
        return { status: "error", reason: "unavailable" };
      }

      const result = await waitForResult(popup);
      if (result.status === "ok") {
        // Scopes may have grown, and a previously dead connection may be alive
        // again; anything cached about either is now wrong.
        forgetAllTokens();
        dispatch(hgApi.util.invalidateTags(["HGConnection", "HGCollection"]));
      }
      return result;
    },
    [dispatch, startAuthorization],
  );
}

export function connectErrorMessage(result: OAuthResult): string | null {
  if (result.status === "ok") return null;
  switch (result.reason) {
    case "cancelled":
      return null; // Closing the window is an answer, not a failure.
    case "popup_blocked":
      return "Allow pop-ups for this site, then try connecting again.";
    case "access_denied":
      return "Permission was declined.";
    case "expired":
      return "That took too long. Try connecting again.";
    case "unavailable":
      return "This server has no credentials configured for that system.";
    case "exchange_failed":
      return "The provider refused the connection. Try again.";
    case "store_failed":
      return "Connected, but the grant could not be saved. Try again.";
    default:
      return "Could not connect. Try again.";
  }
}
